// src/hooks/useHandleAppleSignIn.ts
import { useCallback } from 'react'
import { Capacitor } from '@capacitor/core'
import {
  SignInWithApple,
  SignInWithAppleOptions,
  SignInWithAppleResponse,
} from '@capacitor-community/apple-sign-in'
import { auth } from '../firebase'
import {
  OAuthProvider,
  signInWithCredential,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  GoogleAuthProvider
} from 'firebase/auth'
import { SocialLogin } from '@capgo/capacitor-social-login'


/**
 * ERRORS WE’VE SEEN:
 *  • auth/missing-or-invalid-nonce  → Apple’s ID token nonce (raw) didn’t match our hash
 *  • auth/invalid-credential        → audience/clientId mismatch
 *  • TS complaints around scopes/id_token
 *
 * WHAT CHANGED?
 * 1) **Compute a hex string SHA-256** of the raw nonce (not Base64URL).
 * 2) **Pass that hex string** into the Apple request as `nonce`.
 * 3) **Keep the original** rawNonce to hand back to Firebase.
 * 4) Plugin will forward the hex-hashed nonce → Apple → ID token.
 * 5) Firebase will hash **your rawNonce** to hex and compare to Apple’s `payload.nonce`.
 *
 * Docs (Web): “make sure you're sending the SHA256-hashed nonce as a hex string with your request to Apple”  [oai_citation:0‡Firebase](https://firebase.google.com/docs/auth/web/apple)
 */

function makeRawNonce(len = 32): string {
  const chars =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-._'
  const buf = crypto.getRandomValues(new Uint8Array(len))
  return Array.from(buf).map(i => chars[i % chars.length]).join('')
}

/** SHA-256 → hex (lowercase) */
async function sha256Hex(str: string): Promise<string> {
  const data = new TextEncoder().encode(str)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

export function useHandleAppleSignIn() {
  return useCallback(async () => {
    try {
      console.log('[🍎] START Apple Sign-In')

      if (Capacitor.getPlatform() === 'web') {
        throw new Error('Use your web-fallback for Apple on web.')
      }

      // ── 1) Generate nonces ──
      const rawNonce = makeRawNonce()
      console.log('[🍎] rawNonce =', rawNonce)

      const hashedNonce = await sha256Hex(rawNonce)
      console.log('[🍎] hex-hashed nonce =', hashedNonce)

      // ── 2) Call the native sheet ──
      const opts: SignInWithAppleOptions = {
        clientId:    'com.panpals.app.web',                                  // Service ID (must match Firebase console)
        redirectURI: 'https://crumbly-461ef.firebaseapp.com/__/auth/handler', // same in Apple Dev & Firebase
        scopes:      'email',                                                // plugin wants a string
        nonce:       hashedNonce,                                            // HEX-encoded hash!
      }
      console.log('[🍎] SignInWithApple.authorize opts', opts)
      const res: SignInWithAppleResponse = await SignInWithApple.authorize(opts)
      console.log('[🍎] authorize result', res)

      // ── 3) Extract tokens ──
      const { identityToken, authorizationCode } = res.response
      console.log(
        '[🍎] identityToken (start)…',
        identityToken?.slice(0,10)+'…',
        'authCode (start)…',
        authorizationCode?.slice(0,10)+'…'
      )
      if (!identityToken || !authorizationCode) {
        throw new Error('No identityToken or authorizationCode from Apple')
      }

      // ── 4) Decode & inspect the JWT payload ──
      try {
        const [, payloadB64] = identityToken.split('.')
        const payload = JSON.parse(atob(payloadB64))
        console.log('[🍎] JWT payload =', payload)
        console.log('[🍎] payload.nonce =', payload.nonce)
        // This SHOULD equal `hashedNonce`
      } catch (e) {
        console.warn('[🍎] failed to decode JWT payload for inspection', e)
      }

      // ── 5) Build Firebase credential with the **raw** nonce ──
      const provider = new OAuthProvider('apple.com')
      provider.addScope('email')
      const credential = provider.credential({
        idToken:     identityToken,
        rawNonce,                // your original
        accessToken: authorizationCode,
      })
      console.log('[🍎] built Firebase credential')

      // ── 6) Sign in / link ──
      try {
        const userCred = await signInWithCredential(auth, credential)
        console.log('[🍎] Firebase signed in!', userCred.user.uid)
      } catch (e: any) {
        if (e.code === 'auth/account-exists-with-different-credential') {
          const email = e.customData.email!
          const methods = await fetchSignInMethodsForEmail(auth, email)

          if (methods.includes('google.com')) {
            // sign in with Google and then link Apple
            const res = await SocialLogin.login({ 
              provider: 'google', 
              options: { scopes: ['email'] }
            })
            const googleCred = GoogleAuthProvider.credential((res.result as any).idToken)
            await linkWithCredential(auth.currentUser!, googleCred)
            // now retry Firebase sign-in with the Apple credential
            const finalCred = await signInWithCredential(auth, credential)
            console.log('[🍎] Linked & signed in as', finalCred.user.uid)
          }
          // similarly you could handle 'password' if you wanted to ask the user for credentials
          else {
            throw e
          }
        } else {
          throw e
        }
      }

    } catch (err: any) {
      console.error('[🍎] ERROR', err.code || err.message || err)
      throw err
    }
  }, [])
}